import { GenericTable } from "@shared/components/GenericTable";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Progress } from "@shared/ui/progress";
import {
  Banknote,
  CreditCard,
  RefreshCcw,
  Send,
  Smartphone,
  Monitor,
  FileSignature,
  Target,
} from "lucide-react";
import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

const getChannelIcon = (channel: string) => {
  switch (channel) {
    case "Cash":
      return (
        <div className="w-8 h-8 rounded-lg bg-green-50 text-green-600 flex items-center justify-center">
          <Banknote className="w-4 h-4" />
        </div>
      );
    case "ACH":
      return (
        <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
          <RefreshCcw className="w-4 h-4" />
        </div>
      );
    case "Wire":
      return (
        <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
          <Send className="w-4 h-4" />
        </div>
      );
    case "Check":
      return (
        <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
          <FileSignature className="w-4 h-4" />
        </div>
      );
    case "Card":
      return (
        <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
          <CreditCard className="w-4 h-4" />
        </div>
      );
    case "Zelle":
      return (
        <div className="w-8 h-8 rounded-lg bg-red-50 text-red-600 flex items-center justify-center">
          <Smartphone className="w-4 h-4" />
        </div>
      );
    case "Kiosk":
      return (
        <div className="w-8 h-8 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center">
          <Monitor className="w-4 h-4" />
        </div>
      );
    default:
      return (
        <div className="w-8 h-8 rounded-lg bg-gray-50 text-gray-600 flex items-center justify-center">
          <Monitor className="w-4 h-4" />
        </div>
      );
  }
};

export const ChannelBreakdownList = ({ data }: { data: any }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const currency = useCurrency();
  const columns: TableColumn<any>[] = [
    {
      key: "paymentMode",
      header: "Payment Mode",
      type: "custom",
      render: (value: any) => (
        <div className="flex items-center gap-3">
          {getChannelIcon(value)}
          <span className="font-semibold text-gray-900">{value}</span>
        </div>
      ),
    },
    {
      key: "systemReceipts",
      header: `System Receipts (${currency})`,
      type: "custom",
      render: (value: any) => (
        <span className="font-semibold text-gray-900">{value}</span>
      ),
    },
    {
      key: "bankCredits",
      header: `Bank Credits (${currency})`,
      type: "custom",
      render: (value: any) => (
        <span className="font-semibold text-gray-900">{value}</span>
      ),
    },
    {
      key: "variance",
      header: `Variance (${currency})`,
      type: "custom",
      render: (value: any, row: any) => (
        <span
          className={`font-bold ${row.varianceRaw !== 0 ? "text-red-600" : "text-gray-900"}`}
        >
          {value}
        </span>
      ),
    },
    {
      key: "matchRate",
      header: "Matched",
      type: "custom",
      render: (value: any) => (
        <div className="flex items-center gap-3 w-32">
          <Progress
            value={value}
            className="h-2 flex-1"
            indicatorClassName="bg-green-500"
          />
          <span className="text-sm font-semibold text-gray-700 w-10 text-right">
            {value}%
          </span>
        </div>
      ),
    },
    {
      key: "statusDisplay",
      header: "Status",
      type: "custom",
      render: (value: any) => (
        <Badge
          variant="outline"
          className={`flex items-center gap-1.5 w-max px-2.5 py-0.5 rounded-full font-medium ${
            value === "Matched"
              ? "bg-green-50 text-green-700 border-green-200"
              : "bg-red-50 text-red-700 border-red-200"
          }`}
        >
          <div
            className={`w-1.5 h-1.5 rounded-full ${value === "Matched" ? "bg-green-500" : "bg-red-500"}`}
          ></div>
          {value}
        </Badge>
      ),
    },
  ];

  const actions: TableAction<any>[] = [
    {
      label: "Drill-down",
      icon: <Target className="w-4 h-4 mr-1.5" />,
      onClick: (row: any) =>
        navigate(
          `/cx/payments/day-end-reconcilation/view/${id}/${row.channel}/${row.paymentMode}/drill-down`,
        ),
      variant: "outline",
    },
  ];

  return (
    <div>
      <div className="flex items-center gap-2">
        <Monitor className="w-4 h-4 text-gray-500" />
        <h3 className="font-semibold text-gray-700">Payment Mode Breakdown</h3>
      </div>
      <div className="mt-6">
        <GenericTable
          data={data || []}
          columns={columns}
          actions={actions}
          actionsType="buttons"
          rowKey="id"
        />
      </div>
    </div>
  );
};

export default ChannelBreakdownList;
